import React, { useState } from 'react';
import './StudentRegistrationPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookIcon, DocumentIcon, CreditCardIcon } from '../components/Icons';
import useSEO from '../hooks/useSEO';

function StudentRegistrationPage() {
  useSEO({
    title: "Dossier d'Inscription & Admission Élève | GSU Les Palmiers",
    description: "Complétez votre dossier d'inscription en ligne pour intégrer le Groupe Scolaire et Universitaire Les Palmiers à Togba (Abomey-Calavi).",
    keywords: "dossier inscription élève Bénin, admission lycée Togba, scolarité GSU Palmiers, formulaire inscription 2025-2026",
    canonicalUrl: "https://gsulespalmiers.bj/student-registration"
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Étape 1
    nomTuteur: '',
    telephoneTuteur: '',
    prenom: '',
    nomFamille: '',
    dateNaissance: '',
    lieuNaissance: '',
    genre: '',
    email: '',
    adresse: '',
    // Étape 2
    niveauAdmission: '',
    niveauActuel: '',
    etablissementActuel: '',
    moyenneGenerale: '',
    activitesExtrascolaires: '',
    languesEtrangeres: '',
    // Étape 3
    photoIdentite: null,
    certificatNaissance: null,
    bulletinNotes: null,
    certificatScolarite: null,
    // Étape 4
    optionPaiement: 'trimestriel',
    methodePaiement: 'carte',
    numeroCarte: '',
    dateExpiration: '',
    cvv: '',
    nomCarte: '',
    operateurMobile: '',
    numeroTelephone: ''
  });

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'file' ? files[0] : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      // 🔴 NOUVEAU : Soumettre l'inscription (sans paiement)
      submitInscription();
    }
  };

  const submitInscription = async () => {
    try {
      // Créer un FormData pour envoyer les fichiers
      const formDataToSend = new FormData();
      
      // Ajouter les données textuelles
      formDataToSend.append('nom', formData.nomFamille);
      formDataToSend.append('prenom', formData.prenom);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('telephone', formData.telephone || formData.telephoneTuteur);
      formDataToSend.append('date_naissance', formData.dateNaissance);
      formDataToSend.append('lieu_naissance', formData.lieuNaissance);
      formDataToSend.append('adresse', formData.adresse);
      formDataToSend.append('genre', formData.genre);
      formDataToSend.append('nom_tuteur', formData.nomTuteur);
      formDataToSend.append('telephone_tuteur', formData.telephoneTuteur);
      formDataToSend.append('niveau_etude', formData.niveauAdmission);
      formDataToSend.append('etablissement_actuel', formData.etablissementActuel);
      formDataToSend.append('moyenne_generale', formData.moyenneGenerale);
      formDataToSend.append('activites_extrascolaires', formData.activitesExtrascolaires);
      formDataToSend.append('langues_etrangeres', formData.languesEtrangeres);
      formDataToSend.append('filiere_id', 1); // À adapter selon la filière choisie
      
      // Ajouter les fichiers s'ils existent
      if (formData.photoIdentite) {
        formDataToSend.append('photo_identite', formData.photoIdentite);
      }
      if (formData.certificatNaissance) {
        formDataToSend.append('certificat_naissance', formData.certificatNaissance);
      }
      if (formData.bulletinNotes) {
        formDataToSend.append('bulletin_notes', formData.bulletinNotes);
      }
      if (formData.certificatScolarite) {
        formDataToSend.append('certificat_scolarite', formData.certificatScolarite);
      }

      const response = await fetch('http://127.0.0.1:8000/api/v1/inscriptions', {
        method: 'POST',
        body: formDataToSend // Ne pas mettre de Content-Type, le navigateur le fait automatiquement
      });

      if (response.ok) {
        alert('✅ Inscription soumise avec succès!\n\nVotre demande est en attente de validation par l\'administration.\n\nVous recevrez un email et un SMS avec vos identifiants de connexion une fois votre inscription validée.');
        
        // Rediriger vers la page d'accueil
        if (window.navigateTo) {
          window.navigateTo('home');
        }
      } else {
        const error = await response.json();
        alert('❌ Erreur lors de l\'inscription: ' + (error.message || 'Veuillez réessayer'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de l\'inscription. Veuillez réessayer.');
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Voulez-vous annuler l\'inscription?')) {
      setFormData({
        nomTuteur: '',
        telephoneTuteur: '',
        prenom: '',
        nomFamille: '',
        dateNaissance: '',
        lieuNaissance: '',
        genre: '',
        email: '',
        adresse: ''
      });
    }
  };

  return (
    <div className="student-registration-page">
      <Header />
      
      <section className="registration-content">
        <div className="container">
          <button 
            className="btn-back"
            onClick={() => window.navigateTo && window.navigateTo('login')}
          >
            ← Retour à l'accueil
          </button>

          <div className="steps-indicator">
            <div className={`step ${currentStep === 1 ? 'active' : ''}`}>
              <div className="step-icon">👤</div>
              <div className="step-label">Informations personnelles</div>
            </div>
            <div className="step-divider"></div>
            <div className={`step ${currentStep === 2 ? 'active' : ''}`}>
              <div className="step-icon"><BookIcon size={24} color={currentStep === 2 ? '#fff' : '#0078D7'} /></div>
              <div className="step-label">Informations académiques</div>
            </div>
            <div className="step-divider"></div>
            <div className={`step ${currentStep === 3 ? 'active' : ''}`}>
              <div className="step-icon">📥</div>
              <div className="step-label">Documents</div>
            </div>
          </div>

          <div className="form-container">
            {currentStep === 1 && (
              <>
                <h2>Informations personnelles</h2>
                <p className="form-description">
                  Renseignez vos informations personnelles et celles de votre représentant légal
                </p>

                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Nom du tuteur *</label>
                      <input
                        type="text"
                        name="nomTuteur"
                        placeholder="nom complet"
                        value={formData.nomTuteur}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Téléphone du tuteur *</label>
                      <input
                        type="tel"
                        name="telephoneTuteur"
                        placeholder="+229 XX XX XX XX"
                        value={formData.telephoneTuteur}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Prénom *</label>
                      <input
                        type="text"
                        name="prenom"
                        placeholder="votre prénom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Nom de famille *</label>
                      <input
                        type="text"
                        name="nomFamille"
                        placeholder="votre nom"
                        value={formData.nomFamille}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Date de naissance *</label>
                      <input
                        type="date"
                        name="dateNaissance"
                        placeholder="jj/mm/aaaa"
                        value={formData.dateNaissance}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Lieu de naissance *</label>
                      <input
                        type="text"
                        name="lieuNaissance"
                        placeholder="ville, pays"
                        value={formData.lieuNaissance}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Genre *</label>
                      <select
                        name="genre"
                        value={formData.genre}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Sélectionner votre genre</option>
                        <option value="masculin">Masculin</option>
                        <option value="feminin">Féminin</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="votreemail@gmail.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Adresse complète *</label>
                    <textarea
                      name="adresse"
                      placeholder="Quartier, maison"
                      rows="3"
                      value={formData.adresse}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={handleCancel}
                    >
                      Annuler
                    </button>
                    <button type="submit" className="btn-submit">
                      Suivant
                    </button>
                  </div>
                </form>
              </>
            )}

            {currentStep === 2 && (
              <>
                <h2>Informations académiques</h2>
                <p className="form-description">
                  Indiquez votre parcours scolaire et vos préférences d'orientation
                </p>

                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Niveau d'admission souhaité *</label>
                      <select
                        name="niveauAdmission"
                        value={formData.niveauAdmission}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Sélectionner le niveau</option>
                        <option value="6eme">6ème</option>
                        <option value="5eme">5ème</option>
                        <option value="4eme">4ème</option>
                        <option value="3eme">3ème</option>
                        <option value="seconde">Seconde</option>
                        <option value="premiere">Première</option>
                        <option value="terminale">Terminale</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Niveau actuel *</label>
                      <select
                        name="niveauActuel"
                        value={formData.niveauActuel}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Sélectionner le niveau</option>
                        <option value="6eme">6ème</option>
                        <option value="5eme">5ème</option>
                        <option value="4eme">4ème</option>
                        <option value="3eme">3ème</option>
                        <option value="seconde">Seconde</option>
                        <option value="premiere">Première</option>
                        <option value="terminale">Terminale</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Établissement actuel *</label>
                      <input
                        type="text"
                        name="etablissementActuel"
                        placeholder="votre prénom"
                        value={formData.etablissementActuel}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Moyenne générale *</label>
                      <input
                        type="text"
                        name="moyenneGenerale"
                        placeholder="votre nom"
                        value={formData.moyenneGenerale}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Activité extrascolaire *</label>
                    <textarea
                      name="activitesExtrascolaires"
                      placeholder="Sport, musique, lecture"
                      rows="4"
                      value={formData.activitesExtrascolaires}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label>Langues étrangères étudiées</label>
                    <textarea
                      name="languesEtrangeres"
                      placeholder="Ex: anglais (niveau intermédiaire), espagnol (débutant)"
                      rows="3"
                      value={formData.languesEtrangeres}
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={handlePrevious}
                    >
                      Précédent
                    </button>
                    <button type="submit" className="btn-submit">
                      Suivant
                    </button>
                  </div>
                </form>
              </>
            )}

            {currentStep === 3 && (
              <>
                <h2>Documents</h2>
                <p className="form-description">
                  Téléchargez les documents requis pour votre inscription
                </p>

                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-group">
                    <label>Photo d'identité *</label>
                    <input
                      type="file"
                      name="photoIdentite"
                      accept="image/*"
                      onChange={handleChange}
                      required
                    />
                    <small style={{color: '#666', fontSize: '0.85rem'}}>
                      Format accepté: JPG, PNG (max 2MB)
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Certificat de naissance *</label>
                    <input
                      type="file"
                      name="certificatNaissance"
                      accept=".pdf,.jpg,.png"
                      onChange={handleChange}
                      required
                    />
                    <small style={{color: '#666', fontSize: '0.85rem'}}>
                      Format accepté: PDF, JPG, PNG (max 5MB)
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Bulletin de notes *</label>
                    <input
                      type="file"
                      name="bulletinNotes"
                      accept=".pdf"
                      onChange={handleChange}
                      required
                    />
                    <small style={{color: '#666', fontSize: '0.85rem'}}>
                      Format accepté: PDF (max 5MB)
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Certificat de scolarité *</label>
                    <input
                      type="file"
                      name="certificatScolarite"
                      accept=".pdf,.jpg,.png"
                      onChange={handleChange}
                      required
                    />
                    <small style={{color: '#666', fontSize: '0.85rem'}}>
                      Format accepté: PDF, JPG, PNG (max 5MB)
                    </small>
                  </div>

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-cancel"
                      onClick={handlePrevious}
                    >
                      Précédent
                    </button>
                    <button type="submit" className="btn-submit">
                      ✅ Soumettre l'inscription
                    </button>
                  </div>
                </form>
              </>
            )}

            <div className="login-link">
              J'ai déjà un compte ? <a href="#" onClick={(e) => {
                e.preventDefault();
                alert('Fonctionnalité de connexion à implémenter');
              }}>Se connecter</a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default StudentRegistrationPage;
